import {find, map} from 'lodash';
import {Fragment, useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {StepWizardChildProps} from 'react-step-wizard';

import {CreditCardIcon, DataWrapper, Divider, DotLineDivider, Icon, Radio, WizardFooter} from 'components';
import {PaymentSourceExpiry} from 'components/PaymentSourceExpiry';
import {VALID_PAYMENT_SOURCE_STATUSES} from 'constants/billing-platform';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {BillingPlatformPaymentSource, WizardFooterProps} from 'types';
import {transformUtils} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.USE_EXISTING_CARD;

type Props = {
    maxUsers: number;
    usersToRemove: string[];
    paymentSources: BillingPlatformPaymentSource[];
    selectedPaymentSourceId: string;
    wizardFooterProps: WizardFooterProps;
    existingPaymentCallback: (data: {
        paymentToken?: string;
        maxUsers?: number;
        selectedPaymentSourceId?: string;
        usersToRemove?: string[]
    }) => Promise<any>;
} & Partial<StepWizardChildProps>;

export const SelectExistingPaymentSourcesStep: React.FC<Props> = ({
    maxUsers, usersToRemove, paymentSources, wizardFooterProps, selectedPaymentSourceId,
    nextStep, previousStep, existingPaymentCallback, lastStep
}) => {
    const {t} = useTranslation();
    const [updatedPaymentSourceId, setUpdatedPaymentSourceId] = useState(selectedPaymentSourceId);
    const updatedPaymentSource = useMemo(() =>
        find(paymentSources, {id: updatedPaymentSourceId}), [paymentSources, updatedPaymentSourceId]);

    const {
        send: sendPayment,
        error: paymentError,
        loading: paymentLoading
    } = useApiData(existingPaymentCallback, {
        onSuccess: lastStep
    });

    const isNextDisabled = useMemo(() =>
        updatedPaymentSource && !VALID_PAYMENT_SOURCE_STATUSES.includes(updatedPaymentSource.status) || paymentLoading,
    [updatedPaymentSource, paymentLoading]);

    const chargeExistingCard = useCallback(() => {
        sendPayment({
            maxUsers,
            usersToRemove,
            ...(updatedPaymentSourceId !== selectedPaymentSourceId && {selectedPaymentSourceId: updatedPaymentSourceId})
        });
    }, [sendPayment, maxUsers, usersToRemove, selectedPaymentSourceId, updatedPaymentSourceId]);

    const handleNext = useCallback(() => {
        if (updatedPaymentSourceId) {
            chargeExistingCard();
        } else {
            nextStep();
        }
    }, [updatedPaymentSourceId, chargeExistingCard, nextStep]);

    return (
        <Fragment>
            <div className='tw-text-center'>
                <div className='tw-mb-8'>{t(T_KEYS.TITLE)}</div>
                <DataWrapper isLoading={paymentLoading} hideContentOnLoading={false}>
                    <div className='tw-mx-auto tw-inline-block'>
                        {map(paymentSources, (paymentSource) => (
                            <Radio
                                key={paymentSource.id}
                                name='selected-card'
                                className='tw-mb-4'
                                checked={paymentSource.id === updatedPaymentSourceId}
                                disabled={!VALID_PAYMENT_SOURCE_STATUSES.includes(paymentSource.status)}
                                value={paymentSource.id}
                                onSelect={setUpdatedPaymentSourceId}
                            >
                                <div className='tw-flex tw-items-center tw-ml-4'>
                                    <CreditCardIcon className='tw-mr-2' name={paymentSource.card.brand} />
                                    <div className='tw-flex -sm:tw-flex-col'>
                                        <div className='tw-flex tw-items-center'>
                                            {transformUtils.formatCreditCardLabel(paymentSource.card, t)}
                                            <DotLineDivider className='-sm:tw-hidden tw-mx-2 tw-bg-dark' />
                                        </div>
                                        <PaymentSourceExpiry paymentSource={paymentSource} iconPlacement='right' />
                                    </div>
                                </div>
                            </Radio>
                        ))}
                        <Divider />
                        <div className='tw-mt-4'>
                            <Radio
                                name='selected-card'
                                checked={updatedPaymentSourceId === null}
                                value={null}
                                onSelect={setUpdatedPaymentSourceId}
                            >
                                <div className='tw-flex tw-items-center tw-ml-4'>
                                    <Icon size='3xl' name='add-credit-card' />
                                    <div className='tw-ml-2'>{t(T_KEYS.ADD_NEW_CARD)}</div>
                                </div>
                            </Radio>
                        </div>
                    </div>
                </DataWrapper>
                {paymentError && <div className='tw-text-alert tw-text-lg tw-text-center tw-my-6'>{t(T_KEYS.ERROR)}</div>}
            </div>
            <WizardFooter
                {...wizardFooterProps}
                handlePrevious={previousStep}
                disabledNext={wizardFooterProps.disabledNext || isNextDisabled}
                handleNext={handleNext}
            />
        </Fragment>
    );
};